import { ALL_MEAL_TYPES } from "./utils";
import { normalizeRecipeSteps } from "./recipeSteps";

const VALID_MEAL_TYPES = new Set(ALL_MEAL_TYPES.map((m) => m.id));

/** Jedna surovina v importním JSON (může vytvořit novou položku v katalogu). */
export interface RecipeImportIngredient {
  name: string;
  unit: string;
  quantity: number;
  /** Velikost balení při vytváření nové suroviny. Vynechat = 1 (nebo 0 s byWeight). */
  packageSize?: number;
  /** true = surovina na váhu (packageSize se uloží jako 0). */
  byWeight?: boolean;
}

/** Jeden recept v importním JSON. */
export interface RecipeImportPayload {
  name: string;
  servings?: number;
  /** Postup jako pole kroků (doporučeno). */
  steps?: string[];
  /** Zpětná kompatibilita: jeden text nebo pole řetězců. */
  instructions?: string | string[];
  mealTypes?: string[];
  ingredients?: RecipeImportIngredient[];
}

export type RecipeImportParseResult =
  | { ok: true; recipes: RecipeImportPayload[] }
  | { ok: false; errors: string[] };

/** Ukázkový JSON pro dokumentaci a tlačítko „Vložit ukázku“ v importeru. */
export const RECIPE_IMPORT_EXAMPLE: RecipeImportPayload = {
  name: "Špagety bolognese",
  servings: 4,
  steps: [
    "Uvařte těstoviny podle návodu na obalu.",
    "Na pánvi opečte mleté maso s cibulí, přidejte rajčata a provařte.",
    "Smíchejte s těstovinami a podávejte s parmazánem.",
  ],
  mealTypes: ["obed", "vecere"],
  ingredients: [
    { name: "Špagety", unit: "g", quantity: 400, packageSize: 500 },
    { name: "Mleté maso", unit: "g", quantity: 500, byWeight: true },
    { name: "Rajčata v konzervě", unit: "g", quantity: 400, packageSize: 400 },
    { name: "Cibule", unit: "ks", quantity: 1, packageSize: 1 },
  ],
};

export const RECIPE_IMPORT_EXAMPLE_JSON = JSON.stringify(RECIPE_IMPORT_EXAMPLE, null, 2);

/** Popis polí pro nápovědu v UI. */
export const RECIPE_IMPORT_FIELD_HELP: { field: string; required: boolean; description: string }[] = [
  { field: "name", required: true, description: "Název receptu." },
  { field: "servings", required: false, description: "Počet porcí (výchozí 2). Množství surovin je pro tento počet." },
  { field: "steps", required: false, description: "Postup přípravy jako pole kroků (doporučeno)." },
  { field: "instructions", required: false, description: "Zastaralé — jeden text nebo pole; převede se na kroky." },
  {
    field: "mealTypes",
    required: false,
    description: `Vhodné chody: ${ALL_MEAL_TYPES.map((m) => m.id).join(", ")}. Prázdné = univerzální recept.`,
  },
  { field: "ingredients", required: false, description: "Seznam surovin receptu." },
  { field: "ingredients[].name", required: true, description: "Název suroviny. Při importu lze nespárované mapovat na existující v katalogu." },
  { field: "ingredients[].unit", required: true, description: "Jednotka (g, kg, ml, l, ks, …)." },
  { field: "ingredients[].quantity", required: true, description: "Množství na uvedený počet porcí (kladné číslo)." },
  { field: "ingredients[].packageSize", required: false, description: "Velikost balení pro nově vytvořenou surovinu (výchozí 1)." },
  { field: "ingredients[].byWeight", required: false, description: "true = surovina na váhu (bez balení)." },
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function normalizeName(name: string): string {
  return name.trim().toLocaleLowerCase("cs");
}

function parseIngredient(raw: unknown, path: string, errors: string[]): RecipeImportIngredient | null {
  if (!isRecord(raw)) {
    errors.push(`${path}: očekáván objekt suroviny`);
    return null;
  }
  const name = typeof raw.name === "string" ? raw.name.trim() : "";
  const unit = typeof raw.unit === "string" ? raw.unit.trim() : "";
  if (!name) errors.push(`${path}.name: povinný řetězec`);
  if (!unit) errors.push(`${path}.unit: povinný řetězec`);

  let quantity: number | null = null;
  if (typeof raw.quantity === "number" && Number.isFinite(raw.quantity) && raw.quantity > 0) {
    quantity = raw.quantity;
  } else {
    errors.push(`${path}.quantity: povinné kladné číslo`);
  }

  const byWeight = raw.byWeight === true;
  let packageSize: number | undefined;
  if (raw.packageSize !== undefined) {
    if (typeof raw.packageSize !== "number" || !Number.isFinite(raw.packageSize) || raw.packageSize < 0) {
      errors.push(`${path}.packageSize: musí být nezáporné číslo`);
    } else {
      packageSize = raw.packageSize;
    }
  }

  if (!name || !unit || quantity === null) return null;
  return { name, unit, quantity, packageSize, byWeight };
}

function parseRecipe(raw: unknown, path: string, errors: string[]): RecipeImportPayload | null {
  if (!isRecord(raw)) {
    errors.push(`${path}: očekáván objekt receptu`);
    return null;
  }
  const name = typeof raw.name === "string" ? raw.name.trim() : "";
  if (!name) errors.push(`${path}.name: povinný řetězec`);

  let servings: number | undefined;
  if (raw.servings !== undefined) {
    if (typeof raw.servings !== "number" || !Number.isFinite(raw.servings) || raw.servings <= 0) {
      errors.push(`${path}.servings: musí být kladné číslo`);
    } else {
      servings = raw.servings;
    }
  }

  let steps: string[] | undefined;
  if (raw.steps !== undefined) {
    if (!Array.isArray(raw.steps)) {
      errors.push(`${path}.steps: musí být pole řetězců`);
    } else {
      steps = normalizeRecipeSteps(raw.steps);
    }
  } else if (raw.instructions !== undefined) {
    if (typeof raw.instructions === "string" || Array.isArray(raw.instructions)) {
      steps = normalizeRecipeSteps(raw.instructions);
    } else {
      errors.push(`${path}.instructions: musí být řetězec nebo pole řetězců`);
    }
  }

  let mealTypes: string[] | undefined;
  if (raw.mealTypes !== undefined) {
    if (!Array.isArray(raw.mealTypes)) {
      errors.push(`${path}.mealTypes: musí být pole řetězců`);
    } else {
      mealTypes = [];
      for (const v of raw.mealTypes) {
        if (typeof v === "string" && VALID_MEAL_TYPES.has(v) && !mealTypes.includes(v)) {
          mealTypes.push(v);
        }
      }
    }
  }

  let ingredients: RecipeImportIngredient[] | undefined;
  if (raw.ingredients !== undefined) {
    if (!Array.isArray(raw.ingredients)) {
      errors.push(`${path}.ingredients: musí být pole`);
    } else {
      ingredients = [];
      raw.ingredients.forEach((item, i) => {
        const ing = parseIngredient(item, `${path}.ingredients[${i}]`, errors);
        if (ing) ingredients!.push(ing);
      });
    }
  }

  if (!name) return null;
  return { name, servings, steps, mealTypes, ingredients };
}

/** Parsuje JSON — jeden recept nebo pole receptů. */
export function parseRecipeImportJson(raw: string): RecipeImportParseResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, errors: ["JSON je prázdný"] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, errors: ["Neplatný JSON — zkontrolujte syntaxi (čárky, uvozovky, závorky)."] };
  }

  const errors: string[] = [];
  const recipes: RecipeImportPayload[] = [];

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return { ok: false, errors: ["Pole receptů je prázdné"] };
    parsed.forEach((item, i) => {
      const r = parseRecipe(item, `recepty[${i}]`, errors);
      if (r) recipes.push(r);
    });
  } else {
    const r = parseRecipe(parsed, "recept", errors);
    if (r) recipes.push(r);
  }

  if (errors.length > 0) return { ok: false, errors };
  if (recipes.length === 0) return { ok: false, errors: ["Žádný platný recept k importu"] };
  return { ok: true, recipes };
}

export function resolveIngredientPackageSize(ing: RecipeImportIngredient): number {
  if (ing.byWeight || ing.packageSize === 0) return 0;
  if (typeof ing.packageSize === "number" && ing.packageSize > 0) return ing.packageSize;
  return 1;
}

/** Unikátní surovina z JSON, která v katalogu nebyla nalezena podle názvu. */
export interface UnresolvedIngredient {
  key: string;
  name: string;
  unit: string;
  packageSize?: number;
  byWeight?: boolean;
  usedInRecipes: string[];
}

/** Výsledek analýzy JSON před importem. */
export interface ImportAnalysis {
  recipes: RecipeImportPayload[];
  autoMatched: { key: string; importName: string; ingredientId: string; catalogName: string }[];
  unmatched: UnresolvedIngredient[];
  /** Reprezentativní data suroviny podle klíče (pro vytvoření nové). */
  ingredientDataByKey: Map<string, RecipeImportIngredient>;
}

/** Sbírá unikátní suroviny z receptů a porovná je s katalogem. */
export function analyzeRecipeImport(
  recipes: RecipeImportPayload[],
  existing: { id: string; name: string }[]
): ImportAnalysis {
  const byName = new Map(existing.map((i) => [normalizeName(i.name), { id: i.id, name: i.name }]));
  const ingredientDataByKey = new Map<string, RecipeImportIngredient>();
  const usedInByKey = new Map<string, Set<string>>();

  for (const recipe of recipes) {
    for (const ing of recipe.ingredients ?? []) {
      const key = normalizeName(ing.name);
      if (!ingredientDataByKey.has(key)) ingredientDataByKey.set(key, ing);
      const used = usedInByKey.get(key) ?? new Set<string>();
      used.add(recipe.name);
      usedInByKey.set(key, used);
    }
  }

  const autoMatched: ImportAnalysis["autoMatched"] = [];
  const unmatched: UnresolvedIngredient[] = [];

  for (const [key, ing] of ingredientDataByKey) {
    const hit = byName.get(key);
    if (hit) {
      autoMatched.push({ key, importName: ing.name, ingredientId: hit.id, catalogName: hit.name });
    } else {
      unmatched.push({
        key,
        name: ing.name,
        unit: ing.unit,
        packageSize: ing.packageSize,
        byWeight: ing.byWeight,
        usedInRecipes: [...(usedInByKey.get(key) ?? [])],
      });
    }
  }

  unmatched.sort((a, b) => a.name.localeCompare(b.name, "cs"));
  autoMatched.sort((a, b) => a.importName.localeCompare(b.importName, "cs"));

  return { recipes, autoMatched, unmatched, ingredientDataByKey };
}

export type IngredientResolvePlan =
  | { kind: "existing"; ingredientId: string }
  | { kind: "create"; data: RecipeImportIngredient };

/** Sestaví plán řešení surovin podle automatické shody, ručního mapování a vytvoření nových. */
export function buildResolvePlan(
  analysis: ImportAnalysis,
  userMappings: Record<string, string>,
  keysToCreate: Set<string>
): Map<string, IngredientResolvePlan> {
  const plan = new Map<string, IngredientResolvePlan>();

  for (const m of analysis.autoMatched) {
    plan.set(m.key, { kind: "existing", ingredientId: m.ingredientId });
  }
  for (const u of analysis.unmatched) {
    const mappedId = userMappings[u.key]?.trim();
    if (mappedId) {
      plan.set(u.key, { kind: "existing", ingredientId: mappedId });
    } else if (keysToCreate.has(u.key)) {
      const data = analysis.ingredientDataByKey.get(u.key);
      if (data) plan.set(u.key, { kind: "create", data });
    }
  }
  return plan;
}

export interface ImportRecipeApi {
  createIngredient: (body: {
    name: string;
    unit: string;
    packageSize: number;
  }) => Promise<{ id: string }>;
  createRecipe: (body: {
    name: string;
    servings: number;
    steps: string[];
    mealTypes: string[];
    ingredients: { ingredientId: string; quantity: number }[];
  }) => Promise<{ id: string }>;
}

export interface RecipeImportRunResult {
  recipeIds: string[];
  createdIngredients: string[];
  reusedIngredients: string[];
}

/** Provede import podle připraveného plánu surovin. */
export async function runRecipeImport(
  recipes: RecipeImportPayload[],
  resolvePlan: Map<string, IngredientResolvePlan>,
  apiClient: ImportRecipeApi
): Promise<RecipeImportRunResult> {
  const resolvedIds = new Map<string, string>();
  const createdSet = new Set<string>();
  const reusedSet = new Set<string>();
  const recipeIds: string[] = [];

  async function ingredientIdFor(key: string): Promise<string> {
    const cached = resolvedIds.get(key);
    if (cached) return cached;

    const plan = resolvePlan.get(key);
    if (!plan) throw new Error(`Chybí plán pro surovinu „${key}"`);

    if (plan.kind === "existing") {
      resolvedIds.set(key, plan.ingredientId);
      return plan.ingredientId;
    }

    const res = await apiClient.createIngredient({
      name: plan.data.name.trim(),
      unit: plan.data.unit.trim(),
      packageSize: resolveIngredientPackageSize(plan.data),
    });
    resolvedIds.set(key, res.id);
    createdSet.add(plan.data.name);
    return res.id;
  }

  for (const recipe of recipes) {
    const recipeIngredientRows: { ingredientId: string; quantity: number }[] = [];

    for (const ing of recipe.ingredients ?? []) {
      const key = normalizeName(ing.name);
      const plan = resolvePlan.get(key);
      if (plan?.kind === "existing") reusedSet.add(ing.name);
      const ingredientId = await ingredientIdFor(key);
      recipeIngredientRows.push({ ingredientId, quantity: ing.quantity });
    }

    const res = await apiClient.createRecipe({
      name: recipe.name.trim(),
      servings: recipe.servings && recipe.servings > 0 ? recipe.servings : 2,
      steps: recipe.steps ?? [],
      mealTypes: recipe.mealTypes ?? [],
      ingredients: recipeIngredientRows,
    });
    recipeIds.push(res.id);
  }

  return {
    recipeIds,
    createdIngredients: [...createdSet],
    reusedIngredients: [...reusedSet],
  };
}
